/**
 * The three asset classes the symbol picker toggles between: S, C and P.
 *
 * <h2>The single letter IS the wire value</h2>
 *
 * {@code /api/symbols?market=C} is a valid request — {@code Market.COMMODITIES} carries the code
 * {@code "C"} on the Java side and {@code Market.resolve} accepts it. So a toggle sends its own
 * label and there is no second table mapping a letter to a market id.
 *
 * <p>That is deliberate rather than convenient. A letter-to-id table here would be a copy of one
 * that already exists in `Market`, and the two drifting is how this repository has twice shipped a
 * UI that confidently asked the server for the wrong thing — the deployment-wide `appConfig.market`
 * constant that ignored a customer's profile, and the three DynaMan run paths that each sent a
 * different strategy from one dialog. One string, defined once, resolved once.
 *
 * <h2>Why STOCKS carries no code</h2>
 *
 * Because the equity list is MARKET-dependent and the other two are not. Pressing S must ask for
 * the customer's own market — `US` or `IN`, resolved from their profile — whereas C and P are
 * global and the same for everybody. A code of `"S"` here would invite sending `market=S`, which
 * the backend resolves to nothing and falls back to the US equities for: an India customer would
 * silently get the US list, which is the exact bug the profile override was written to fix.
 */
export const ASSET_CLASSES = [
  {
    key: 'S',
    label: 'S',
    name: 'Stocks',
    // null = "ask for the customer's own market". See above.
    marketParam: null,
    tooltip: 'Stocks — equities and ETFs for your market',
  },
  {
    key: 'C',
    label: 'C',
    name: 'Commodities',
    marketParam: 'C',
    defaultTicker: 'XAUUSD',
    tooltip: 'Commodities — metals (gold, silver, platinum, palladium, copper)',
  },
  {
    key: 'P',
    label: 'P',
    name: 'Currency Pairs',
    marketParam: 'P',
    defaultTicker: 'EURUSD',
    tooltip: 'Currency Pairs — USD majors, regional pairs and crosses',
  },
];

/** The class the picker opens on. Stocks, because that is what the product has always been. */
export const DEFAULT_ASSET_CLASS = 'S';

/**
 * Stored symbols whose feed carries NO traded volume — the JavaScript side of
 * {@code AssetCatalogue.reportsVolume}.
 *
 * <h3>Why the chart needs this and cannot ask the data</h3>
 *
 * Deciding from the bars ("every volume is 0, so hide the pane") was the obvious alternative and
 * it is subtly wrong in both directions: an equity whose history is genuinely all zeros — HPIL is
 * one, with 2,089 such rows — would lose its pane for a data defect rather than a property of the
 * instrument, and an empty or not-yet-loaded series trivially satisfies "every value is zero".
 * Whether an instrument HAS a volume feed is a fact about the instrument, so it is declared.
 *
 * <h3>Why a second copy of the list is acceptable here</h3>
 *
 * It normally would not be. What makes it safe is that `assetClasses.test.js` reads
 * `AssetCatalogue.java` off disk and asserts this set equals the Java catalogue exactly — so the
 * two cannot drift without a test failing. Java cannot hand an array to JavaScript, and the
 * alternative (a round trip per symbol, through a new public endpoint that must be registered in
 * two properties files) buys nothing the test does not already guarantee.
 */
export const VOLUME_LESS_SYMBOLS = new Set([
  // Metals (C)
  'XAUUSD', 'XAGUSD', 'XPTUSD', 'XPDUSD', 'XCUUSD',
  // USD majors (P)
  'EURUSD', 'USDJPY', 'GBPUSD', 'AUDUSD', 'USDCAD', 'USDCHF', 'NZDUSD',
  // Emerging / regional USD (P)
  'USDCNY', 'USDHKD', 'USDKRW', 'USDSGD', 'USDMXN', 'USDINR',
  // Major crosses (P)
  'EURJPY', 'GBPJPY', 'EURGBP', 'EURCHF', 'AUDJPY', 'EURAUD', 'EURCAD',
]);

/**
 * Whether this symbol's feed carries a traded volume.
 *
 * <p>TRUE for anything not listed — an unknown symbol is an equity, which is what every other
 * symbol in this product already is. That matches {@code AssetCatalogue.reportsVolume}, and it is
 * the safe direction: a new instrument nobody declared keeps the volume pane it has always had,
 * rather than silently losing it.
 */
export function symbolReportsVolume(symbol) {
  if (!symbol) return true;
  return !VOLUME_LESS_SYMBOLS.has(String(symbol).trim().toUpperCase());
}

/** One entry by key, or the default when the key is unrecognised. */
export function assetClassFor(key) {
  return ASSET_CLASSES.find((a) => a.key === key)
    || ASSET_CLASSES.find((a) => a.key === DEFAULT_ASSET_CLASS);
}

/**
 * The {@code market=} value to send for a class, given the customer's own market.
 *
 * @param key           'S' | 'C' | 'P'
 * @param ownMarketCode the market the customer's profile resolves to, e.g. 'US' or 'IN'
 */
export function marketParamFor(key, ownMarketCode) {
  const entry = assetClassFor(key);
  return entry.marketParam || ownMarketCode;
}

/**
 * The symbol to open a class on, or null to let the caller take the first of the list.
 *
 * <p>Null for stocks because that answer depends on the market — SPY for US, NSEI for India — and
 * `defaultTickerForMarket` already owns it. Returning a guess here would override it.
 */
export function defaultTickerForAssetClass(key) {
  return assetClassFor(key).defaultTicker || null;
}
